// @flow
import * as React from 'react';
import {useEffect, useState} from 'react';
import {Body} from 'sail/Text';
import {ContentGroupItem} from 'docs/util';
import * as Sentry from '@sentry/react';
import Spinner from 'sail/Spinner';
import Box from 'sail/Box';
import {Table, TabGroup, TableHeaderCell, TableCell} from 'docs/components';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useAccount, useApi} from 'docs/data';
import type {
  PayoutsPayoutCountriesContextSettlementCurrencyByCountryModel,
  GetConnectPayoutCurrenciesEndpointConnectPayoutCountries,
  GetConnectPayoutCurrenciesEndpointConnectSettlementCurrencyByCountryModel,
} from 'docs/types';

const messages = defineMessages({
  header: {
    id: 'docsShell.payoutSettlementCurrencies.header',
    description: 'Introduces a table of settlement currencies',
    defaultMessage:
      'Accounts created in {countryName} can receive payouts in the following settlement currencies.',
  },
  nonLocalHeader: {
    id: 'docsShell.payoutSettlementCurrencies.nonLocalHeader',
    description: 'Introduces a table of non-local settlement currencies',
    defaultMessage:
      'Accounts created in {countryName} can receive payouts in the following non-local settlement currencies.',
  },
  connectHeader: {
    id: 'docsShell.payoutSettlementCurrencies.connectHeader',
    description: 'Introduces a table of non-local settlement currencies',
    defaultMessage:
      'Accounts created in {countryName} and are under the <a>{type} service agreement</a> can receive payouts in the following non-local settlement currencies.',
  },
  dropDownLabel: {
    id: 'docsShell.payoutSettlementCurrencies.dropDownLabel',
    description: 'Label for country drop down selector',
    defaultMessage:
      'Viewing free supported settlement currencies for Stripe accounts in',
  },
  nonLocalDropDownLabel: {
    id: 'docsShell.payoutSettlementCurrencies.nonLocalDropDownLabel',
    description: 'Label for alternative currencies country drop down selector',
    defaultMessage:
      'Viewing supported settlement currencies for Stripe accounts in',
  },
  settlementCurrency: {
    id: 'docsShell.payoutSettlementCurrencies.settlementCurrency',
    description: 'Labels a table column of currencies',
    defaultMessage: 'Settlement currency',
  },
  countries: {
    id: 'docsShell.payoutSettlementCurrencies.countries',
    description: 'Labels a table column of lists of country names',
    defaultMessage: 'Can be paid out to banks in these countries for free',
  },
  nonLocalCountries: {
    id: 'docsShell.payoutSettlementCurrencies.nonLocalCountries',
    description:
      'Labels a table column of lists of country names for paid payouts',
    defaultMessage: 'Can be paid out to banks in these countries',
  },
  minManual: {
    id: 'docsShell.payoutSettlementCurrencies.min',
    description:
      'Labels a table column that has minimum manual payout amount (amount + currency)',
    defaultMessage: 'Minimum manual payout amount',
  },
  minAuto: {
    id: 'docsShell.payoutSettlementCurrencies.max',
    description:
      'Labels a table column that has minimum auto payout amount (amount + currency)',
    defaultMessage: 'Minimum auto payout amount',
  },
  pricing: {
    id: 'docsShell.payoutSettlementCurrencies.pricing',
    description: 'Labels a table column that has the pricing information',
    defaultMessage: 'Pricing',
  },
  cost: {
    id: 'docsShell.payoutSettlementCurrencies.cost',
    description: 'The cost that it takes to settle in a specific currency',
    defaultMessage: '{percent}%, minimum fee of {cost}',
  },
  loadingSettlementCurrencies: {
    id: 'docsShell.payoutSettlementCurrencies.loading',
    description: 'A loading message displayed to a user while a table loads',
    defaultMessage: 'Loading a table of settlement currencies',
  },
  errorLoadingSettlementCurrencies: {
    id: 'docsShell.payoutSettlementCurrencies.error',
    description:
      'An error message displayed to a user while a table fails to load',
    defaultMessage: 'Unable to load the table of settlement currencies',
  },
  loadingConnectCurrencies: {
    id: 'docsShell.connectPayoutCurrencies.loading',
    description: 'A loading message displayed to a user while a table loads',
    defaultMessage: 'Loading a table of connect payout currencies',
  },
  errorLoadingConnectCurrencies: {
    id: 'docsShell.connectPayoutCurrencies.error',
    description:
      'An error message displayed to a user while a table fails to load',
    defaultMessage: 'Unable to load the table of connect payout currencies',
  },
  fullService: {
    id: 'docsShell.payoutSettlementCurrencies.fullService',
    description: 'A label for a tab',
    defaultMessage: 'Full Service Agreement',
  },
  recipientService: {
    id: 'docsShell.payoutSettlementCurrencies.recipientService',
    description: 'A label for a tab',
    defaultMessage: 'Recipient Service Agreement',
  },
});

type DropdownProps = {|
  items: Array<ContentGroupItem>,
  defaultValue?: string,
|};

const Dropdown = ({items, defaultValue}: DropdownProps) => {
  const intl = useIntl();
  const dropDownLabel = intl.formatMessage(messages.dropDownLabel);

  return (
    <TabGroup
      selector="drop-down"
      items={items}
      label={dropDownLabel}
      defaultValue={defaultValue}
    />
  );
};

const NonLocalDropdown = ({items, defaultValue}: DropdownProps) => {
  const intl = useIntl();
  const nonLocalDropDownLabel = intl.formatMessage(
    messages.nonLocalDropDownLabel,
  );

  return (
    <TabGroup
      selector="drop-down"
      items={items}
      label={nonLocalDropDownLabel}
      defaultValue={defaultValue}
    />
  );
};

/**
 * Tables on /docs/payouts
 **/
const ConnectCurrencyTabContent = ({type, countryName, rows}) => {
  return (
    <>
      <Body>
        <FormattedMessage
          {...messages.connectHeader}
          values={{
            countryName,
            type,
            a: (str) => (
              <a
                href={`/docs/connect/service-agreement-types#${type}`}
                target="_blank"
              >
                {str}
              </a>
            ),
          }}
        />
      </Body>
      <CurrencyTable countryName={countryName} rows={rows} type={type} />
    </>
  );
};

type CurrencyTableProps = {|
  countryName: string,
  rows: Array<any>,
  type?: string,
|};

const CurrencyTable = ({countryName, rows, type}: CurrencyTableProps) => {
  const tableRows = type ? rows.filter((r) => r.type === type) : rows;

  return (
    <Table type="solid">
      <thead>
        <tr>
          <TableHeaderCell>
            <FormattedMessage {...messages.settlementCurrency} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.countries} />
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {tableRows.map((r, i) => (
          <tr key={`${countryName}-${r.currency}`}>
            <TableCell>{r.currency.toUpperCase()}</TableCell>
            <TableCell>{r.supported_payout_countries?.join(', ')}</TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

type SettlementCurrenciesTableProps = {|
  currencies: Array<PayoutsPayoutCountriesContextSettlementCurrencyByCountryModel>,
|};

export const SettlementCurrenciesTable = ({
  currencies,
}: SettlementCurrenciesTableProps) => {
  const {account} = useAccount();
  const defaultValue = account.country.code;

  if (!currencies.length) {
    return null;
  }
  const items = currencies.map((c) => {
    if (!c.countries.length) {
      return null;
    }

    return new ContentGroupItem(
      c.code,
      `${c.name || ''} (${c.code})`,
      (<CurrencyTable countryName={c.name} rows={c.countries} />),
    );
  });

  return <Dropdown items={items.filter(Boolean)} defaultValue={defaultValue} />;
};

export const NonLocalPayoutSettlementCurrencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(false);
  const {account} = useAccount();
  const {api} = useApi();
  const accountCountry = account.country.code;

  useEffect(() => {
    // This table takes a LONG time
    // Load it async so we don't block the page
    api
      .getNonLocalPayouts({country: accountCountry})
      .then((response) => {
        setCurrencies(response.countries);
      })
      .catch((err) => {
        setError(true);
        if (Sentry && Sentry.captureMessage) {
          Sentry.captureMessage(
            `Failed to load nonlocal settlement currencies table`,
          );
        }
      });
  }, [api, accountCountry]);

  if (error) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Body>
          <FormattedMessage {...messages.errorLoadingSettlementCurrencies} />
        </Body>
      </Box>
    );
  }

  if (!currencies.length) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Spinner
          text={<FormattedMessage {...messages.loadingSettlementCurrencies} />}
          size="large"
        />
      </Box>
    );
  }

  const items = currencies.map((c) => {
    if (!c.countries.length) {
      return null;
    }

    return new ContentGroupItem(
      c.code,
      `${c.name || ''} (${c.code})`,
      (
        <NonLocalSettlementCurrencyTable
          countryName={c.name}
          rows={c.countries}
        />
      ),
    );
  });

  return (
    <NonLocalDropdown
      items={items.filter(Boolean)}
      defaultValue={accountCountry}
    />
  );
};

type NonLocalSettlementCurrencyTableProps = {|
  countryName: string,
  rows: Array<any>,
|};

// Used on /docs/payouts
const NonLocalSettlementCurrencyTable = ({
  countryName,
  rows,
}: NonLocalSettlementCurrencyTableProps) => {
  if (!rows) {
    return null;
  }

  return (
    <>
      <Body>
        <FormattedMessage {...messages.nonLocalHeader} values={{countryName}} />
      </Body>
      <Table type="solid">
        <thead>
          <tr>
            <TableHeaderCell>
              <FormattedMessage {...messages.settlementCurrency} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.nonLocalCountries} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.minManual} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.minAuto} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.pricing} />
            </TableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${countryName}-${r.currency}`}>
              <TableCell>{r.currency.toUpperCase()}</TableCell>
              <TableCell>{r.supported_payout_countries?.join(', ')}</TableCell>
              <TableCell>{r.minimum_manual_payout_amount}</TableCell>
              <TableCell>{r.minimum_auto_payout_amount}</TableCell>
              <TableCell>
                <FormattedMessage
                  {...messages.cost}
                  values={{percent: r.percent, cost: r.cost}}
                />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

/**
 * Tables on /docs/connect/bank-debit-card-payouts
 **/
export const ConnectCurrencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(false);
  const {api} = useApi();

  useEffect(() => {
    // This table takes a LONG time
    // Load it async so we don't block the page
    api
      .getConnectPayoutCurrencies()
      .then((response) => {
        setCurrencies(response.countries);
      })
      .catch((err) => {
        setError(true);
        if (Sentry && Sentry.captureMessage) {
          Sentry.captureMessage(`Failed to load connect currencies table`);
        }
      });
  }, [api]);

  if (error) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Body>
          <FormattedMessage {...messages.errorLoadingConnectCurrencies} />
        </Body>
      </Box>
    );
  }

  if (!currencies.length) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Spinner
          text={<FormattedMessage {...messages.loadingConnectCurrencies} />}
          size="large"
        />
      </Box>
    );
  }

  return <ConnectCurrenciesTable currencies={currencies} />;
};

type ConnectCurrenciesTableProps = {|
  currencies: Array<GetConnectPayoutCurrenciesEndpointConnectSettlementCurrencyByCountryModel>,
|};

const ConnectCurrenciesTable = ({currencies}: ConnectCurrenciesTableProps) => {
  const intl = useIntl();
  const {account} = useAccount();
  const defaultValue = account.country.code;
  const dropDownLabel = intl.formatMessage(messages.dropDownLabel);

  if (!currencies.length) {
    return null;
  }

  const items = currencies.map((c) => {
    if (!c.countries.length) {
      return null;
    }

    return new ContentGroupItem(
      c.code,
      `${c.name || ''} (${c.code})`,
      (<ConnectCurrencyTable countryName={c.name} rows={c.countries} />),
    );
  });

  return (
    <TabGroup
      selector="drop-down"
      items={items.filter(Boolean)}
      label={dropDownLabel}
      defaultValue={defaultValue}
    />
  );
};

type ConnectCurrencyTableProps = {|
  countryName: string,
  rows: Array<GetConnectPayoutCurrenciesEndpointConnectPayoutCountries>,
|};

const ConnectCurrencyTable = ({
  countryName,
  rows,
}: ConnectCurrencyTableProps) => {
  const intl = useIntl();
  const fullServiceTitle = intl.formatMessage(messages.fullService);
  const recipientServiceTitle = intl.formatMessage(messages.recipientService);

  const tabSet = [
    {id: 'full', title: fullServiceTitle},
    {id: 'recipient', title: recipientServiceTitle},
  ];

  const tabs = [];
  tabSet.forEach((tab) => {
    if (rows.filter((r) => r.type === tab.id).length > 0) {
      tabs.push(
        new ContentGroupItem(
          tab.id,
          tab.title,
          (
            <ConnectCurrencyTabContent
              type={tab.id}
              rows={rows}
              countryName={countryName}
            />
          ),
        ),
      );
    }
  });

  if (!tabs.length) {
    return null;
  }

  return <TabGroup items={tabs} selector="tabs" />;
};
